import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import textversion from 'textversionjs';
import { flatten } from 'ramda';
import { escape } from 'html-escaper';
import { truncate } from '@moda/portal-stanchions';
import { CustomPageQuery, EditorialCellFragment } from '../../generated/types';
import { PageMeta } from '../../components/PageMeta';

type DisplayPage = NonNullable<CustomPageQuery['displayPage']>;

type Props = {
  displayPage: DisplayPage;
};

export const EditorialPageMeta: React.FC<Props> = ({ displayPage }) => {
  const { slug = '' } = useParams<{ slug?: string }>();

  const pumoMetaDescription =
    displayPage.meta_description !== '' ? displayPage.meta_description : null;

  const moduleDescription = useMemo(() => {
    if (slug === 'lauren-santo-domingo')
      return 'Shop an edit of designer fashion, jewelry, & home decor curated by Lauren Santo Domingo.';

    const maxDescriptionLength = 160;
    const taglines = displayPage.targetBuckets
      .filter(bucket => bucket.name !== 'banner')
      .map(bucket =>
        bucket.modules.map(module => {
          switch (module.__typename) {
            case 'SinglePumoModule':
              return module.slides.map(slide => slide.slideText?.taglineText?.body);
            case 'MultiPumoModule':
              return module.units.map(unit => unit.slideText?.taglineText?.body);
            case 'CarouselPumoModule':
              return module.cells
                .filter(cell => cell.__typename === 'Slide')
                .map(cell => (cell as EditorialCellFragment).slideText?.taglineText?.body);
          }
        })
      );

    if (!taglines) return '';
    const firstTagline = flatten(taglines).filter(Boolean)[0];
    if (!firstTagline) return '';
    const taglineText = escape(textversion(firstTagline));
    return truncate(taglineText, maxDescriptionLength);
  }, [slug, displayPage]);

  const description = pumoMetaDescription ? pumoMetaDescription : moduleDescription;

  return <PageMeta title={displayPage.name} description={description} />;
};
